import { useEffect, useRef } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CategoryField } from 'categories/components';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext, useNotificationContext } from 'core/states';
import { useSavePart } from 'parts/hooks';
import { IPartDetails, IPartManufacturer } from 'parts/types';
import { Size } from 'theme/enums';

import { buildSavePayload } from './mappers';
import { IEditPartSetupFormValues } from './types';

interface IProps {
  part?: IPartDetails | null;
  manufacturer?: IPartManufacturer | null;
  isOpen: boolean;
  onClose: () => void;
  defaultValues: IEditPartSetupFormValues;
}

export const EditPartSetupFormFields = ({
  part,
  manufacturer,
  isOpen,
  onClose,
  defaultValues,
}: IProps) => {
  const { t } = useTranslation();

  const { isSubmitting } = useFormContext();
  const { clearNotification } = useNotificationContext();

  const { savePart } = useSavePart();

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current != null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearCloseTimeout();
  }, []);

  const handleOnClose = (reset?: UseFormReset<IEditPartSetupFormValues>, withoutReset = false) => {
    clearCloseTimeout();
    clearNotification();
    onClose();

    if (!withoutReset) {
      reset?.(defaultValues);
    }
  };

  const onSubmit =
    (reset: UseFormReset<IEditPartSetupFormValues>) =>
    async (formValues: IEditPartSetupFormValues) => {
      if (!part) {
        return false;
      }

      const payload = buildSavePayload(part, manufacturer, formValues);

      if (!payload) {
        return false;
      }

      const { success } = await savePart(payload);

      if (!success) {
        return false;
      }

      clearCloseTimeout();
      closeTimeoutRef.current = setTimeout(() => handleOnClose(reset, true), 1000);
    };

  return (
    <Form
      title={t('titlePartSetup')}
      isOpen={isOpen}
      onClose={handleOnClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelSaveChanges')}
      cancelBtnLabel={t('buttonLabelCancel')}
      disabledSubmit={isSubmitting || !part}
      level={1}
      size={Size.MD}
      shouldSubmitOnKeyDown={false}
    >
      <FormField
        name="description"
        type={InputType.TEXTAREA}
        inputProps={{
          label: t('labelDescription'),
          minRows: 3,
        }}
      />
      <FormField
        name="orderingNumber"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelOrderingNumber'),
        }}
      />
      <FormField
        name="engineeringNumber"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelEngineeringNumber'),
        }}
      />
      <CategoryField
        name="category"
        placeholder={t('labelAllParts')}
        allItemsLabel={t('labelAllParts')}
      />
    </Form>
  );
};
